/**
 * „Podobne ćwiczenia" — four movements under the hero, and the one thing on this card that waits.
 *
 * ── It is the SECOND read, so it has a reserve and a gate of its own ────────────────────────
 * Everything else in the theatre arrives with the catalogue. This does not: `GET /exercise/{id}/similar`
 * is the backend's own answer and the phone reads the same endpoint, so deriving it here from the
 * catalogue already in the cache would be two clients disagreeing about what is similar to a bench
 * press. The strip therefore holds `--h-ex-theatre-similar` before it knows anything, and its
 * skeleton stands INSIDE that reserve — the hero above it is `flex: 1` against the same box, so a
 * strip that grew when its answer landed would shrink the picture under the trainer's eyes.
 *
 * ── The sparkle is a GREY pill, and that is a decision rather than a shade ──────────────────
 * Owner decision D6: never an accent wash. The accent marks a real action or a real state, and „the
 * app worked this out for you" is neither — it is a footnote about where the list came from. The
 * pill is `--bg-chip` on `--border` with `--text-2` ink, which is the same neutral chip the muscle
 * pills beside it wear.
 *
 * ── A card SWAPS the theatre's subject, it does not open a second one ───────────────────────
 * `onPick` is `useTheatre`'s `goTo`, which replaces the address rather than pushing it: a trainer
 * who followed four suggestions must not press Back four times to get out of the card.
 */

import { useTranslation } from 'react-i18next';

import { exerciseTypeColour } from '~/entities/exercise-library';
import { ExerciseTypeGlyph } from '~/entities/exercise-library';
import skel from '~/shared/ui/skeleton.module.css';

import type { ExerciseRow } from '../model/exerciseRow';

import { ExerciseStill } from './ExerciseCells';
import { SparkleGlyph } from './ExerciseLibraryIcons';
import styles from './SimilarStrip.module.css';

/** How many bars the wait draws. The same four the answer draws, so the box cannot change shape. */
const SKELETON_CARDS = [0, 1, 2, 3];

interface SimilarStripProps {
  readonly status: 'pending' | 'error' | 'ready';
  readonly cards: readonly ExerciseRow[];
  /** `useSkeletonGate`'s answer for this read alone — the hero does not wait, so it is not shared. */
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly onPick: (exerciseId: number) => void;
}

export function SimilarStrip({ status, cards, skeleton, reveal, onPick }: SimilarStripProps) {
  const { t } = useTranslation();

  return (
    <section className={styles.strip} aria-label={t('web:exerciseLibrary.theatre.sections.similar')}>
      <div className={styles.head}>
        <span className={styles.sparkle} aria-hidden>
          <SparkleGlyph className={styles.sparkleGlyph} />
        </span>
        <h3 className={styles.title}>{t('web:exerciseLibrary.theatre.sections.similar')}</h3>
        <span className={styles.hint}>{t('web:exerciseLibrary.theatre.similarHint')}</span>
      </div>

      {/* `pending`, `error` and `empty` are three states. The box holds the same height in all four,
        * so what changes between them is ink — including inside the gate's grace window, where the
        * box stands EMPTY rather than flashing four bars for a read that answered in 90ms. */}
      {status === 'pending' ? (
        <div className={styles.cards} aria-hidden>
          {skeleton &&
            SKELETON_CARDS.map((index) => (
              <span key={index} className={styles.card}>
                <span className={`${String(skel.skeleton)} ${String(styles.skelStill)}`} />
                <span className={`${String(skel.skeleton)} ${String(styles.skelName)}`} />
              </span>
            ))}
        </div>
      ) : status === 'error' ? (
        <p className={styles.absence}>{t('web:exerciseLibrary.theatre.similarError')}</p>
      ) : cards.length === 0 ? (
        <p className={styles.absence}>{t('web:exerciseLibrary.theatre.similarEmpty')}</p>
      ) : (
        <div className={`${String(styles.cards)} ${reveal}`}>
          {cards.map((card) => (
            <button
              key={card.id}
              type="button"
              className={styles.card}
              onClick={() => {
                onPick(card.id);
              }}
            >
              <ExerciseStill row={card} className={String(styles.still)} strokeWidth={1.4} />
              <span className={styles.name}>{card.name}</span>
              {/* The type as its glyph and its word, in its own hue — the same pair the bar, the
                * panel and the hero draw (D17). */}
              <span className={styles.typeMark} style={exerciseTypeColour(card.typeKey)}>
                <ExerciseTypeGlyph type={card.typeKey} className={styles.typeGlyph} />
                {card.typeLabel}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
